import React, { useCallback, useEffect, useRef, useState } from 'react';

import { motion, useAnimation } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { ReactComponent as SelectedIcon } from '@pkg/assets/icons/selected.svg';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';
import useIsClickedOutside from '@pkg/hooks/useIsClickedOutside';
import { useMediaQuery } from '@pkg/providers';

import { isEnterKey } from '@app/pages/Prototype/NftManagerPanel/utils';
import { updateNFTManagerFilters } from '@app/store/actions/nft-manager';
import {
    type NFTManagerFilter,
    type NFTManagerSortField,
    type NFTManagerSortOrder,
} from '@app/store/types/nft-manager';

import {
    DropdownIndicator,
    DropdownItem,
    DropdownItemTitle,
    DropdownLabel,
    DropdownList,
    DropdownMenu,
    DropdownTriggerItem,
    DropdownWrapper,
    SortButton,
    SortButtonsGroup,
    SortIndicator,
    StyledArrow,
} from './styles';

interface SortFields {
    id: NFTManagerSortField;
    label: string;
}

interface SortOrder {
    id: NFTManagerSortOrder;
    label: string;
}

const I18N_PREFIX = 'yat_nft_manager_sort_dropdown_';

const NFT_SORT_FIELDS: SortFields[] = [
    { id: 'activity_date', label: I18N_PREFIX + 'option_date_added' },
    { id: 'floor_price', label: I18N_PREFIX + 'option_floor_price' },
    // { id: 'gains', label: I18N_PREFIX + 'option_gains' },
    { id: 'purchase_date', label: I18N_PREFIX + 'option_price_purchased' },
    // { id: 'timescore', label: I18N_PREFIX + 'option_timescore' }
];

const NFT_SORT_ORDERS: SortOrder[] = [
    { id: 'Desc', label: I18N_PREFIX + 'descending' },
    { id: 'Asc', label: I18N_PREFIX + 'ascending' },
];

export function SortDropdown() {
    const { t: translate } = useTranslation(['yat_nft_manager']);

    const { tablet } = useMediaQuery();
    const sortingMotionControls = useAnimation();

    const dispatch = useAppDispatch();
    const currentFilters = useAppSelector((state) => state.nftManager.managerFilters);

    const wrapperDivRef = useRef<HTMLUListElement>(null);

    const [selectedField, setSelectedField] = useState<SortFields>(NFT_SORT_FIELDS[0]);
    const [selectedOrder, setSelectedOrder] = useState<SortOrder>(NFT_SORT_ORDERS[0]);
    const [isVisible, setIsVisible] = useState(false);

    const updateQueryFilters = (filters?: NFTManagerFilter) => {
        dispatch(
            updateNFTManagerFilters({
                filter: filters,
            }),
        );
    };

    useIsClickedOutside(
        wrapperDivRef.current,
        useCallback(
            (isClickedOutside) => {
                if (isClickedOutside && isVisible) {
                    setIsVisible(false);
                }
            },
            [isVisible],
        ),
    );

    useEffect(() => {
        if (!currentFilters.sortingOrder) return;

        const order = NFT_SORT_ORDERS.find((i) => i.id === currentFilters.sortingOrder);
        if (order) setSelectedOrder(order);
        // Need delay to allow setState to occur
        const controlsTimeout = setTimeout(() => {
            sortingMotionControls.start('animate');
        }, 200);
        return () => clearTimeout(controlsTimeout);
    }, [currentFilters, sortingMotionControls]);

    useEffect(() => {
        if (!currentFilters.sortingField) return;

        const field = NFT_SORT_FIELDS.find((i) => i.id === currentFilters.sortingField);
        if (field) setSelectedField(field);
    }, [currentFilters]);

    const updateSelectedOrder = (dir: SortOrder) => {
        setSelectedOrder(dir);
        sortingMotionControls.start('animate');

        updateQueryFilters({
            sortingField: currentFilters?.sortingField || NFT_SORT_FIELDS[0].id,
            sortingOrder: dir.id,
        });
    };

    const updateSelectedField = (field: SortFields) => {
        setSelectedField(field);
        setIsVisible(false);

        updateQueryFilters({
            sortingField: field.id,
            sortingOrder: currentFilters?.sortingOrder || NFT_SORT_ORDERS[0].id,
        });
    };

    const onTriggerClick = useCallback(() => setIsVisible((prevVisible) => !prevVisible), []);

    return (
        <DropdownWrapper>
            <DropdownLabel aria-hidden="false" id="sort-dropdown-label">
                {translate(I18N_PREFIX + 'label')}
            </DropdownLabel>

            <DropdownList ref={wrapperDivRef}>
                <DropdownTriggerItem
                    role="button"
                    aria-labelledby="sort-dropdown-label"
                    tabIndex={0}
                    $open={isVisible}
                    onClick={onTriggerClick}
                    onKeyUp={(event) => {
                        if (isEnterKey(event.key)) onTriggerClick();
                    }}
                >
                    {translate(selectedField.label)}

                    <motion.span
                        animate={{ rotate: isVisible ? 180 : 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                    >
                        <DropdownIndicator />
                    </motion.span>
                </DropdownTriggerItem>

                <DropdownMenu aria-expanded={isVisible} role="list" $expanded={isVisible}>
                    <ul>
                        {NFT_SORT_FIELDS.map((field, index) => (
                            <DropdownItem
                                key={index}
                                tabIndex={isVisible ? 0 : -1}
                                $active={field.id === selectedField.id}
                                onClick={() => updateSelectedField(field)}
                                onKeyUp={(event) => {
                                    if (isEnterKey(event.key)) updateSelectedField(field);
                                }}
                            >
                                <DropdownItemTitle>{translate(field.label)}</DropdownItemTitle>
                                {field.id === selectedField.id && <SelectedIcon />}
                            </DropdownItem>
                        ))}
                    </ul>
                </DropdownMenu>
            </DropdownList>

            <SortButtonsGroup
                key={`tablet - ${tablet}`}
                animate={sortingMotionControls}
                onTapStart={() => sortingMotionControls.start('tapping')}
                onTap={() => {
                    // Need delay to allow setState to occur on button click on children
                    setTimeout(() => {
                        sortingMotionControls.start('animate');
                    }, 1);
                }}
                onMouseLeave={() => sortingMotionControls.start('animate')}
            >
                <SortIndicator
                    variants={{
                        tapping: {
                            width: 50,
                            borderRadius: '100px',
                            translateX: selectedOrder.id === 'Asc' ? 30 : 0,
                        },
                        animate: {
                            width: 40,
                            borderRadius: '50%',
                            translateX: selectedOrder.id === 'Asc' ? 40 : 0,
                        },
                    }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                />

                {NFT_SORT_ORDERS.map((direction) => (
                    <SortButton
                        key={direction.id}
                        size={tablet ? 'small' : 'medium'}
                        color="clear"
                        variant="solid"
                        aria-label={`Select ${translate(direction.label)}`}
                        onClick={() => updateSelectedOrder(direction)}
                        onKeyUp={(ev) => {
                            isEnterKey(ev.key) && updateSelectedOrder(direction);
                        }}
                    >
                        <StyledArrow $pointingUp={direction.id === 'Asc'} />
                    </SortButton>
                ))}
            </SortButtonsGroup>
        </DropdownWrapper>
    );
}
